import { Bindable, NotifyProperty, nameof } from "bindable-data";
import { inject, injectable, initializer } from "@launchtray/tsyringe-async";
import { INetworkClient } from "../../../../networking/INetworkClient";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { ILatestNewsModel } from "./ILatestNewsModel";
import { LatestNewsStatus } from "./LatestNewsStatus";
import { LatestNews } from "./LatestNews";
import { LatestNewsExpandedPopupInfo } from "./LatestNewsExpandedPopupInfo";
import { Ticker } from "pixi.js";
import { IConnectionModel } from "by/gamefactory/cityversetycoon/networking/IConnectionModel";
import { PromiseCompletionSource } from "async/PromiseCompletionSource";

@injectable()
export class LatestNewsModel extends Bindable implements ILatestNewsModel {
    private static readonly UPDATE_DELAY_MS = 30000;

    private readonly networkClient: INetworkClient;
    private readonly logger: ILogger;
    private readonly connectionModel: IConnectionModel;

    private updateTime: number = 0;
    private getStatusPCS: PromiseCompletionSource<void>;

    public readonly latestNewsStatus: NotifyProperty<LatestNewsStatus>;
    public readonly latestNews: NotifyProperty<LatestNews>;
    public readonly latestNewsInfo: NotifyProperty<LatestNewsExpandedPopupInfo>;

    public constructor(
        @inject("INetworkClient") networkClient: INetworkClient,
        @inject("ILogger") logger: ILogger,
        @inject("IConnectionModel") connectionModel: IConnectionModel
    ) {
        super();
        this.networkClient = networkClient;
        this.logger = logger;
        this.connectionModel = connectionModel;
        this.latestNewsStatus = new NotifyProperty(this, nameof(this, "latestNewsStatus"), null);
        this.latestNews = new NotifyProperty(this, nameof(this, "latestNews"), null);
        this.latestNewsInfo = new NotifyProperty(this, nameof(this, "latestNewsInfo"), null);
        Ticker.shared.add(this.onTick, this);
    }

    public async getLatestNewsStatus(): Promise<void> {
        this.updateTime = 0;
        if (!this.connectionModel.connected.value) {
            return;
        }
        if (this.getStatusPCS != null) {
            return this.getStatusPCS.result;
        }
        this.getStatusPCS = new PromiseCompletionSource();
        const status = await this.networkClient.latestNewsStatus();
        if (status != undefined) {
            this.latestNewsStatus.setValue(new LatestNewsStatus(status));
        }
        this.getStatusPCS.complete();
        this.getStatusPCS = null;
    }

    public async getLatestNews(setAsRead?: boolean): Promise<void> {
        const latestNewsResult = await this.networkClient.getLatestNews(setAsRead);
        if (latestNewsResult != undefined) {
            this.latestNews.setValue(new LatestNews(latestNewsResult));
        }
    }

    public async getLatestNewsInfoFromId(latestNewsId: string): Promise<void> {
        const latestNewsInfoResult = await this.networkClient.getLatestNewsInfoFromId(latestNewsId);
        if (latestNewsInfoResult != undefined) {
            this.latestNewsInfo.setValue(new LatestNewsExpandedPopupInfo(latestNewsInfoResult));
        }
    }

    private async onTick(): Promise<void> {
        this.updateTime = Math.min(this.updateTime + Ticker.shared.deltaMS, LatestNewsModel.UPDATE_DELAY_MS);
        if (this.updateTime === LatestNewsModel.UPDATE_DELAY_MS) {
            await this.getLatestNewsStatus();
        }
    }
}
